import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Star, PaperPlaneTilt, Copy, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Compact widget shown on the chantier overview: aggregates every rated
 * intervention of the chantier + lets the admin (re)send the rating link
 * manually per intervention.
 */
export default function ChantierRatingsCard({ chantierId, interventions = [], isAdmin }) {
  const [ratings, setRatings] = useState({}); // { iv_id: {rating, comment, rated_at, rating_token, ...} }
  const [busy, setBusy] = useState(null);

  const load = async () => {
    // Include interventions that are done OR already carry rating data
    // (so admin sees the badge as soon as a link is generated / a note lands).
    const relevant = interventions.filter((i) => i.status === "done" || i.rating || i.rating_token || i.rating_sent_at);
    if (relevant.length === 0) return;
    const results = await Promise.all(
      relevant.map((iv) => api.get(`/interventions/${iv.id}/rating`).then((r) => [iv.id, r.data]).catch(() => [iv.id, null])),
    );
    const map = {};
    for (const [k, v] of results) if (v) map[k] = v;
    setRatings(map);
  };

  useEffect(() => { load(); }, [chantierId, interventions.length]);  // reload when list changes

  const send = async (ivId) => {
    setBusy(ivId);
    try {
      const r = await api.post(`/interventions/${ivId}/send-review-link`);
      if (r.data?.ok) toast.success(`Lien envoyé par ${r.data.channel === "sms" ? "SMS" : "WhatsApp"}`);
      else toast.error(`Envoi impossible : ${r.data?.reason || "erreur"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const copyLink = async (ivId) => {
    try {
      const r = await api.post(`/interventions/${ivId}/generate-review-link`);
      if (r.data?.url) {
        try { await navigator.clipboard.writeText(r.data.url); toast.success("Lien copié"); }
        catch { toast.success("Lien généré : " + r.data.url); }
        load();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doneIvs = interventions.filter((iv) => iv.status === "done" || ratings[iv.id]?.rating || ratings[iv.id]?.rating_token);
  if (doneIvs.length === 0) return null;

  const rated = doneIvs.filter((iv) => ratings[iv.id]?.rating);
  const avg = rated.length ? (rated.reduce((s, iv) => s + ratings[iv.id].rating, 0) / rated.length).toFixed(2) : null;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="chantier-ratings-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-semibold text-sm">
          <Star size={16} weight="duotone" className="text-amber-300" /> Avis client
        </div>
        {avg != null && (
          <div className="text-xs text-white/60">
            <span className="text-amber-300 font-mono font-semibold">{avg}/5</span> · {rated.length} avis
          </div>
        )}
      </div>

      <div className="space-y-2">
        {doneIvs.map((iv) => {
          const r = ratings[iv.id] || {};
          const stars = r.rating || 0;
          return (
            <div key={iv.id} className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2" data-testid={`chantier-rating-iv-${iv.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{iv.title || "Intervention"}</div>
                <div className="text-[10px] text-white/45">
                  {(iv.scheduled_date || "").slice(0, 10)}
                  {r.rating_sent_at && <> · <PaperPlaneTilt size={10} weight="bold" className="inline text-emerald-300" /> lien envoyé</>}
                </div>
                {r.rating_comment && (
                  <div className="mt-1 text-[11px] text-white/75 italic truncate">« {r.rating_comment} »</div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                {stars > 0 ? (
                  <div className="flex items-center gap-0.5" data-testid={`chantier-rating-stars-${iv.id}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={11} weight={n <= stars ? "fill" : "regular"} className={n <= stars ? "text-amber-400" : "text-white/15"} />
                    ))}
                    <span className="ml-1 text-[10px] font-mono text-white/60">{stars}/5</span>
                  </div>
                ) : (
                  <div className="text-[10px] text-white/40 italic">En attente</div>
                )}
                {isAdmin && stars === 0 && (
                  <>
                    <button
                      type="button"
                      onClick={() => send(iv.id)}
                      disabled={busy === iv.id}
                      className="h-7 px-2 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 text-emerald-200 text-[10px] disabled:opacity-40 flex items-center gap-1"
                      title="Envoyer le lien au client"
                      data-testid={`chantier-rating-send-${iv.id}`}
                    >
                      <PaperPlaneTilt size={10} weight="bold" /> Envoyer
                    </button>
                    <button
                      type="button"
                      onClick={() => copyLink(iv.id)}
                      className="h-7 w-7 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 text-white/60 flex items-center justify-center"
                      title="Copier le lien"
                      data-testid={`chantier-rating-copy-${iv.id}`}
                    >
                      <Copy size={11} weight="bold" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
